<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import validations, { getSectorNameErrorMessage } from './validations';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  selectedSector: {
    type: Object,
    default: () => ({}),
  },
});
const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const name = ref('');
const description = ref('');
const selectedTeams = ref([]);

const v$ = useVuelidate(validations, { name, description });

const uiFlags = computed(() => getters['sectors/getUIFlags'].value);
const teamsList = computed(() => getters['teams/getTeams'].value);

// Teams already in this sector (excluding the auto-created default team)
// or teams not assigned to any sector
const availableTeams = computed(() =>
  teamsList.value.filter(t =>
    t.id !== props.selectedSector.default_team_id &&
    (!t.sector_id || t.sector_id === props.selectedSector.id)
  )
);

const nameErrorMessage = computed(() =>
  t(getSectorNameErrorMessage(v$.value))
);

onMounted(() => {
  name.value = props.selectedSector.name || '';
  description.value = props.selectedSector.description || '';
  selectedTeams.value = teamsList.value.filter(t =>
    t.sector_id === props.selectedSector.id &&
    t.id !== props.selectedSector.default_team_id
  );
});

const onClose = () => emit('close');

const editSector = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  try {
    await store.dispatch('sectors/update', {
      id: props.selectedSector.id,
      name: name.value,
      description: description.value,
      team_ids: selectedTeams.value.map(t => t.id),
    });
    await store.dispatch('teams/get');
    useAlert(t('SECTOR_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    onClose();
  } catch (error) {
    useAlert(error?.message || t('SECTOR_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="$t('SECTOR_MGMT.EDIT.TITLE')" />
    <form class="flex flex-wrap mx-0" @submit.prevent="editSector">
      <woot-input
        v-model="name"
        :class="{ error: v$.name.$error }"
        class="w-full"
        :label="$t('SECTOR_MGMT.FORM.NAME.LABEL')"
        :placeholder="$t('SECTOR_MGMT.FORM.NAME.PLACEHOLDER')"
        :error="nameErrorMessage"
        @input="v$.name.$touch"
        @blur="v$.name.$touch"
      />
      <woot-input
        v-model="description"
        class="w-full"
        :label="$t('SECTOR_MGMT.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('SECTOR_MGMT.FORM.DESCRIPTION.PLACEHOLDER')"
      />

      <div class="w-full mb-4">
        <label class="block text-sm font-medium text-n-slate-12 mb-2">
          {{ $t('SECTOR_MGMT.FORM.TEAMS.LABEL') }}
        </label>
        <multiselect
          v-model="selectedTeams"
          :options="availableTeams"
          track-by="id"
          label="name"
          multiple
          :close-on-select="false"
          :clear-on-select="false"
          hide-selected
          :placeholder="$t('SECTOR_MGMT.FORM.TEAMS.PLACEHOLDER')"
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
        >
          <template #noOptions>{{ $t('FORMS.MULTISELECT.NO_OPTIONS') }}</template>
        </multiselect>
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('SECTOR_MGMT.FORM.CANCEL')"
          @click.prevent="onClose"
        />
        <Button
          :label="$t('SECTOR_MGMT.FORM.SAVE')"
          :disabled="v$.name.$invalid || uiFlags.isUpdating"
          :is-loading="uiFlags.isUpdating"
          @click.prevent="editSector"
        />
      </div>
    </form>
  </div>
</template>
